import { Box, IconButton, Tooltip, Badge, Button, Divider, Chip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PanToolIcon from '@mui/icons-material/PanTool';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/People';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SettingsIcon from '@mui/icons-material/Settings';
import CallEndIcon from '@mui/icons-material/CallEnd';
import NoiseAwareIcon from '@mui/icons-material/NoiseAware';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

export default function MeetingControls({
  audioEnabled,
  videoEnabled,
  screenSharing,
  handRaised,
  chatOpen,
  participantsOpen,
  subtitlesOpen,
  effectsActive,
  effectsSupported = true,
  noiseSuppressionActive = false,
  vadActive = false,
  participantCount,
  unreadMessages,
  isHost,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleHand,
  onToggleChat,
  onToggleParticipants,
  onToggleSubtitles,
  onToggleEffects,
  onOpenSettings,
  onLeave,
  onEndMeeting,
}) {
  const ControlButton = ({ active, activeColor = 'primary', onClick, tooltip, icon, badge }) => (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={onClick}
        sx={{
          bgcolor: active ? `${activeColor}.main` : 'surface.light',
          color: active ? `${activeColor}.contrastText` : 'text.primary',
          '&:hover': {
            bgcolor: active ? `${activeColor}.dark` : 'surface.main',
          },
          width: 48,
          height: 48,
        }}
      >
        {badge ? (
          <Badge badgeContent={badge} color="error">
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 2,
        px: 3,
        bgcolor: 'background.paper',
        borderTop: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* Main controls */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          <ControlButton
            active={!audioEnabled}
            activeColor="error"
            onClick={onToggleAudio}
            tooltip={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            icon={audioEnabled ? <MicIcon /> : <MicOffIcon />}
          />
          {noiseSuppressionActive && audioEnabled && (
            <Tooltip title="AI Noise Suppression Active">
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              >
                <NoiseAwareIcon sx={{ fontSize: 12, color: 'white' }} />
              </Box>
            </Tooltip>
          )}
          {vadActive && audioEnabled && (
            <Tooltip title="Voice Detection Active">
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  left: -2,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              >
                <GraphicEqIcon sx={{ fontSize: 12, color: 'white' }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <ControlButton
          active={!videoEnabled}
          activeColor="error"
          onClick={onToggleVideo}
          tooltip={videoEnabled ? 'Turn off camera' : 'Turn on camera'}
          icon={videoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
        />

        {effectsSupported && (
          <ControlButton
            active={effectsActive}
            activeColor="secondary"
            onClick={onToggleEffects}
            tooltip="Effetti Video"
            icon={<AutoFixHighIcon />}
          />
        )}

        <ControlButton
          active={screenSharing}
          onClick={onToggleScreenShare}
          tooltip={screenSharing ? 'Stop sharing' : 'Share screen'}
          icon={screenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
        />

        <ControlButton
          active={handRaised}
          activeColor="warning"
          onClick={onToggleHand}
          tooltip={handRaised ? 'Lower hand' : 'Raise hand'}
          icon={<PanToolIcon />}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

      {/* Side panel controls */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <ControlButton
          active={chatOpen}
          onClick={onToggleChat}
          tooltip="Chat"
          icon={<ChatIcon />}
          badge={unreadMessages > 0 ? unreadMessages : null}
        />

        <ControlButton
          active={participantsOpen}
          onClick={onToggleParticipants}
          tooltip={`Participants (${participantCount})`}
          icon={<PeopleIcon />}
          badge={participantCount}
        />

        <ControlButton
          active={subtitlesOpen}
          onClick={onToggleSubtitles}
          tooltip="Live Transcription"
          icon={<SubtitlesIcon />}
        />

        <ControlButton
          onClick={onOpenSettings}
          tooltip="Settings"
          icon={<SettingsIcon />}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

      {/* Leave/End buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {isHost ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<CallEndIcon />}
            onClick={onEndMeeting}
            sx={{ borderRadius: 6 }}
          >
            End Meeting
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            startIcon={<CallEndIcon />}
            onClick={onLeave}
            sx={{ borderRadius: 6 }}
          >
            Leave
          </Button>
        )}
      </Box>
    </Box>
  );
}
